import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent, MouseEvent, PointerEvent, ReactNode, RefObject } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { DEFAULT_DOCK_POSITION, DOCK_MARGIN, clampDockBottom, dockSideFor } from '@/lib/companion';
import type { DockPosition, DockSide, ReportTone } from '@/lib/companion';
import { DOCK_HEIGHT } from './useCompanionDock';
import type { DockViewport } from './useCompanionDock';

/** 按下後移動超過這個距離才算拖曳，手稍微晃一下仍然是點擊。 */
const DRAG_THRESHOLD = 6;
/** 頭像探出卡片上緣的高度；拖到最上面時頭也要留在畫面內。 */
const HEAD_ROOM = DOCK_HEIGHT - 64;
const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';
const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), Math.max(min, max));

interface Drag { id: number; x: number; y: number; moved: boolean; rect: DOMRect }

/** 電腦版浮動小哨：點一下開面板，按住可以拖到左右兩側任一高度，放開後吸附到較近的一側。 */
export function CompanionDock({
  buttonRef, label, status, tone, attention, side, bottom, viewport, motion, nudge,
  onMove, onDragStart, onActivate, onGreet, children,
}: {
  buttonRef: RefObject<HTMLButtonElement>;
  label: string; status: string; tone: ReportTone; attention: number;
  side: DockSide; bottom: number; viewport: DockViewport; motion: boolean; nudge: number;
  onMove: (next: DockPosition) => void; onDragStart: () => void;
  onActivate: (event: MouseEvent<HTMLButtonElement>) => void; onGreet: () => void;
  children: ReactNode;
}) {
  const drag = useRef<Drag | null>(null);
  const flipFrom = useRef<DOMRect | null>(null);
  const droppedAt = useRef(-Infinity);
  const [dragging, setDragging] = useState(false);
  const [settle, setSettle] = useState(0);
  const [announcement, setAnnouncement] = useState('');

  // 放開或用鍵盤移動後，從原本的位置滑到新位置，看得出小哨去了哪裡
  useLayoutEffect(() => {
    const node = buttonRef.current;
    const from = flipFrom.current;
    flipFrom.current = null;
    if (!node || !from || reducedMotion()) return;
    const to = node.getBoundingClientRect();
    const dx = from.left - to.left;
    const dy = from.top - to.top;
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
    node.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: 200, easing: EASE_OUT });
  }, [buttonRef, side, bottom, settle]);

  // 待處理的事變多時輕推一下；什麼時候加 nudge 由 SystemCompanion 決定，第一次載入不算
  useEffect(() => {
    const node = buttonRef.current;
    if (!nudge || !node || !motion || reducedMotion() || drag.current?.moved) return;
    node.animate([{ transform: 'none' }, { transform: 'translateY(-6px)' }, { transform: 'none' }], { duration: 240, easing: EASE_OUT });
  }, [buttonRef, nudge, motion]);

  // 拖得快時，第一次移動小哨就離開了按下的點，Chromium 會改從那個點底下的連結或圖片開始原生拖放，
  // 接著送出 pointercancel 讓小哨彈回原位。所以按住小哨期間，頁面上的原生拖放一律擋掉。
  useEffect(() => {
    const block = (event: Event) => { if (drag.current) event.preventDefault(); };
    document.addEventListener('dragstart', block, true);
    return () => document.removeEventListener('dragstart', block, true);
  }, []);

  function place(next: DockPosition, from: DOMRect, message: string) {
    flipFrom.current = from;
    setSettle((value) => value + 1);
    onMove(next);
    setAnnouncement(message);
  }

  function onPointerDown(event: PointerEvent<HTMLButtonElement>) {
    if (event.button !== 0 || !event.isPrimary) return;
    const node = event.currentTarget;
    // 滑動或輕推還沒跑完就被抓住時，先讓動畫跑完，量到的位置才和拖曳中寫入的 transform 對得上
    for (const animation of node.getAnimations()) animation.finish();
    drag.current = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false, rect: node.getBoundingClientRect() };
    // 拖得快時游標會離開卡片，捕捉後移動事件仍然送到這裡
    try { node.setPointerCapture(event.pointerId); } catch { /* 指標已經放開時不能捕捉，當成一般點擊。 */ }
  }

  function onPointerMove(event: PointerEvent<HTMLButtonElement>) {
    const current = drag.current;
    if (!current || current.id !== event.pointerId) return;
    const dx = event.clientX - current.x;
    const dy = event.clientY - current.y;
    if (!current.moved) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      current.moved = true;
      setDragging(true);
      onDragStart();
    }
    const { rect } = current;
    const x = clamp(dx, viewport.edge + DOCK_MARGIN - rect.left, viewport.width - DOCK_MARGIN - rect.right);
    const y = clamp(dy, DOCK_MARGIN + HEAD_ROOM - rect.top, viewport.height - DOCK_MARGIN - rect.bottom);
    // 拖曳中直接改 transform，不經過 React 重繪，才跟得上手
    event.currentTarget.style.transform = `translate(${x}px, ${y}px)`;
  }

  function endDrag(event: PointerEvent<HTMLButtonElement>, commit: boolean) {
    const current = drag.current;
    if (!current || current.id !== event.pointerId) return;
    drag.current = null;
    const node = event.currentTarget;
    if (node.hasPointerCapture(event.pointerId)) node.releasePointerCapture(event.pointerId);
    if (!current.moved) return;
    // 放開後瀏覽器仍會送出 click，onClick 看到這個時間就擋掉，面板才不會跟著打開
    droppedAt.current = performance.now();
    setDragging(false);
    const from = node.getBoundingClientRect();
    node.style.transform = '';
    if (!commit) {
      flipFrom.current = from;
      setSettle((value) => value + 1);
      return;
    }
    const next: DockPosition = {
      side: dockSideFor(from.left + from.width / 2, viewport.edge, viewport.width),
      bottom: clampDockBottom(viewport.height - from.bottom, viewport.height, DOCK_HEIGHT),
    };
    place(next, from, next.side === side ? '小哨換了位置' : `小哨移到${next.side === 'left' ? '左' : '右'}側`);
  }

  function onClick(event: MouseEvent<HTMLButtonElement>) {
    if (performance.now() - droppedAt.current < 300) {
      event.preventDefault();
      return;
    }
    onActivate(event);
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const step = event.shiftKey ? 96 : 24;
    let next: DockPosition;
    let message: string;
    switch (event.key) {
      case 'ArrowUp': next = { side, bottom: clampDockBottom(bottom + step, viewport.height, DOCK_HEIGHT) }; message = '小哨往上移'; break;
      case 'ArrowDown': next = { side, bottom: clampDockBottom(bottom - step, viewport.height, DOCK_HEIGHT) }; message = '小哨往下移'; break;
      case 'ArrowLeft': next = { side: 'left', bottom }; message = '小哨移到左側'; break;
      case 'ArrowRight': next = { side: 'right', bottom }; message = '小哨移到右側'; break;
      case 'Home': next = DEFAULT_DOCK_POSITION; message = '小哨回到右下角'; break;
      default: return;
    }
    event.preventDefault();
    if (next.side === side && next.bottom === bottom) return;
    place(next, event.currentTarget.getBoundingClientRect(), message);
  }

  return <>
    <Dialog.Trigger asChild>
      <button ref={buttonRef} type="button" className="companion-dock" data-side={side} data-dragging={dragging || undefined}
        data-motion={motion ? undefined : 'off'} style={{ '--dock-bottom': `${bottom}px` } as CSSProperties}
        aria-label={label} aria-describedby="companion-dock-hint" aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight Home"
        onPointerDown={onPointerDown} onPointerMove={onPointerMove}
        onPointerUp={(event) => endDrag(event, true)} onPointerCancel={(event) => endDrag(event, false)}
        onLostPointerCapture={(event) => endDrag(event, true)}
        onClick={onClick} onKeyDown={onKeyDown}
        onPointerEnter={() => { if (!drag.current) onGreet(); }} onFocus={onGreet}>
        <span className="companion-dock-pet">{children}</span>
        <span className="companion-dock-text">
          <span className="companion-dock-name">小哨</span>
          <span className="companion-dock-status" data-tone={tone}>{status}</span>
        </span>
        {attention > 0 && <span className="companion-nav-badge companion-dock-badge" aria-hidden="true">{attention > 9 ? '9+' : attention}</span>}
      </button>
    </Dialog.Trigger>
    <span id="companion-dock-hint" hidden>可以拖曳移動；選取後也能用方向鍵移動，按 Home 回到右下角。</span>
    <span className="sr-only" aria-live="polite">{announcement}</span>
  </>;
}
